import { BufferGeometry, Geometry, Line, LineBasicMaterial, Mesh, MeshBasicMaterial, Raycaster, SphereGeometry, Triangle, Vector2, Vector3, } from "three";
const raycaster = new Raycaster();
export function documentXYtoUV(location, surface) {
    // Convert the location in document space to UV coordinates
    if (surface.trim) {
        const width = surface.trim.bottomRightX - surface.trim.topLeftX;
        const height = surface.trim.bottomRightY - surface.trim.topLeftY;
        const u = (location.x - surface.trim.topLeftX) / width;
        const v = 1.0 - (location.y - surface.trim.topLeftY) / height;
        return { u, v };
    }
    const u = location.x / surface.fullBleed.width;
    const v = 1.0 - location.y / surface.fullBleed.height;
    return { u, v };
}
export function uvToDocumentXY(uv, surface) {
    // Convert the location in document space to UV coordinates
    if (surface.trim) {
        const width = surface.trim.bottomRightX - surface.trim.topLeftX;
        const height = surface.trim.bottomRightY - surface.trim.topLeftY;
        const x = uv.u * width + surface.trim.topLeftX;
        const y = (1.0 - uv.v) * height + surface.trim.topLeftY;
        return new Vector2(x, y);
    }
    const x = uv.u * surface.fullBleed.width;
    const y = (1.0 - uv.v) * surface.fullBleed.height;
    return new Vector2(x, y);
}
// Traces a canvas position to geometry in the scene
export function TraceIntersection(position, camera, scene) {
    // Update the ray caster to the current position
    raycaster.setFromCamera(position, camera);
    // Recursively find all intersections from the casted ray
    return raycaster.intersectObjects(scene.children, true);
}
// Traces a canvas position to geometry in the scene
export function TraceIntersectionFromPosition(position, direction, scene) {
    // Update the ray caster to the current position
    raycaster.set(position, direction);
    // Recursively find all intersections from the casted ray
    return raycaster.intersectObjects(scene.children, true);
}
// TODO: This should make a copy and return a new geometry back instead of modifying the parameter
// Maps a specific face of a geometry to provided UV coordinates
export function UVMapFace(geometry, faceId, minU, maxU, minV, maxV) {
    // Left triangle
    geometry.faceVertexUvs[0][faceId * 2][0] = new Vector2(minU, maxV); // 0 1
    geometry.faceVertexUvs[0][faceId * 2][1] = new Vector2(minU, minV); // 0 0
    geometry.faceVertexUvs[0][faceId * 2][2] = new Vector2(maxU, maxV); // 1 1
    // Right triangle
    geometry.faceVertexUvs[0][faceId * 2 + 1][0] = new Vector2(minU, minV); // 0 0
    geometry.faceVertexUvs[0][faceId * 2 + 1][1] = new Vector2(maxU, minV); // 1 0
    geometry.faceVertexUvs[0][faceId * 2 + 1][2] = new Vector2(maxU, maxV); // 1 1
}
// Maps a plane with provided UV coordinates
export function UVMapPlane(geometry, minU, maxU, minV, maxV) {
    return UVMapFace(geometry, 0, minU, maxU, minV, maxV);
}
// Maps each face of a box normalized to it's largest dimension
export function UVMapBox(geometry) {
    const maxDimension = Math.max(geometry.parameters.width, geometry.parameters.height, geometry.parameters.depth);
    // Left & right faces
    UVMapFace(geometry, 0, 0, geometry.parameters.depth / maxDimension, 0, geometry.parameters.height / maxDimension);
    UVMapFace(geometry, 1, 0, geometry.parameters.depth / maxDimension, 0, geometry.parameters.height / maxDimension);
    // Top & bottom faces
    UVMapFace(geometry, 2, 0, geometry.parameters.width / maxDimension, 0, geometry.parameters.depth / maxDimension);
    UVMapFace(geometry, 3, 0, geometry.parameters.width / maxDimension, 0, geometry.parameters.depth / maxDimension);
    // Front & back faces
    UVMapFace(geometry, 4, 0, geometry.parameters.width / maxDimension, 0, geometry.parameters.height / maxDimension);
    UVMapFace(geometry, 5, 0, geometry.parameters.width / maxDimension, 0, geometry.parameters.height / maxDimension);
}
// UV maps and offsets a shaped geometry defined with all coordinates relative to 0,0 as bottom left
export function UVMapOffsetShape(geometry, width, height, offset) {
    const faceUvs = geometry.faceVertexUvs;
    for (let i = 0; i < faceUvs.length; i++) {
        for (let j = 0; j < faceUvs[i].length; j++) {
            for (let k = 0; k < faceUvs[i][j].length; k++) {
                faceUvs[i][j][k].x = (faceUvs[i][j][k].x - offset.x) / width;
                faceUvs[i][j][k].y = (faceUvs[i][j][k].y - offset.y) / height;
            }
        }
    }
}
// UV maps a shaped geometry defined with all coordinates relative to 0,0 as bottom left
export function UVMapShape(geometry, width, height) {
    UVMapOffsetShape(geometry, width, height, new Vector2());
}
export function UVMapExtrudedShape(geometry, size) {
    const cos45 = 0.70710678;
    const up = new Vector3(0, 1, 0);
    const faceUvs = geometry.faceVertexUvs;
    for (let i = 0; i < faceUvs.length; i++) {
        for (let j = 0; j < faceUvs[i].length; j++) {
            // https://github.com/mrdoob/three.js/issues/4994
            const faceNormal = geometry.faces[j].normal;
            const horizontal = Math.abs(faceNormal.dot(up)) < cos45;
            for (let k = 0; k < faceUvs[i][j].length; k++) {
                // This face is part of the front or back, just use it's width and height
                // to properly map the uv coordinates
                if (faceNormal.z === 1 || faceNormal.z === -1) {
                    faceUvs[i][j][k].x = faceUvs[i][j][k].x / size.width;
                    faceUvs[i][j][k].y = faceUvs[i][j][k].y / size.height;
                }
                else {
                    // Otherwise determine if the face is horizontal or vertical and
                    // use the corresponding width and height
                    const scalar = horizontal ? size.height : size.width;
                    faceUvs[i][j][k].x = faceUvs[i][j][k].x / scalar;
                    faceUvs[i][j][k].y = faceUvs[i][j][k].y / scalar;
                }
            }
        }
    }
}
// Recursively disposes geometry in a provided object
export function disposeGeometryRecursively(object3D) {
    if (!object3D)
        return;
    // The object has children that need disposable
    if (object3D.children) {
        object3D.children.forEach((child) => disposeGeometryRecursively(child));
    }
    // The object is a mesh and can have two types of disposal based on type
    if (object3D instanceof Mesh) {
        if (object3D.geometry instanceof Geometry) {
            object3D.geometry.dispose();
        }
        else if (object3D.geometry instanceof BufferGeometry) {
            object3D.geometry.dispose();
        }
    }
}
function buildMeshMapRecursively(meshMap, object3D) {
    if (!object3D)
        return;
    // The object has children that need to be added recursively
    if (object3D.children) {
        object3D.children.forEach((child) => buildMeshMapRecursively(meshMap, child));
    }
    if (object3D instanceof Mesh) {
        meshMap[object3D.uuid] = object3D;
    }
}
// Builds a mapping of each surface id to mesh object in a scene
export function BuildMeshMap(geometry) {
    const meshMap = {};
    buildMeshMapRecursively(meshMap, geometry);
    return meshMap;
}
function createSphere(position, scene, color, radius = 1) {
    const sphere = new Mesh(new SphereGeometry(radius, 32, 16), new MeshBasicMaterial({ color }));
    sphere.position.x = position.x;
    sphere.position.y = position.y;
    sphere.position.z = position.z;
    scene.add(sphere);
}
function createLine(start, end, scene, color) {
    const line = new Line(new BufferGeometry().setFromPoints([start, end]), new LineBasicMaterial({ color }));
    scene.add(line);
}
function weightedAverage(vertex1, vertex2, vertex3, uTarget, vTarget, scene, debugVisuals) {
    // THREE Triangle object has a barycentric interpolation method (.getInterpolation) that does exactaly what we want
    const uv1 = new Vector3(vertex1.u, vertex1.v, 0);
    const uv2 = new Vector3(vertex2.u, vertex2.v, 0);
    const uv3 = new Vector3(vertex3.u, vertex3.v, 0);
    const target = new Vector3(uTarget, vTarget, 0);
    const uvTri = new Triangle(uv1, uv2, uv3);
    const average = new Vector3();
    // Unfortunately, .getInterpolation is only in the newer versions of THREE. But it's just a new name for .getUV
    // Weirldy, here we use getUV to get the XYZ from the UV rather that the other way around
    // TODO: replace .getUV with .getInterpolation after upgrading THREE.js, then remove the ts-ignore.
    uvTri.getUV(target, 
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    vertex1.position, vertex2.position, vertex3.position, average);
    // Optional spheres to help visualize the position in world space
    if (debugVisuals) {
        // Sphere for each vertex
        createSphere(vertex1.position, scene, 0xff0000);
        createSphere(vertex2.position, scene, 0xff0000);
        createSphere(vertex3.position, scene, 0xff0000);
        // Sphere for the position
        createSphere(average, scene, 0xffff00, 2.5);
    }
    return average;
}
function pointNormalFromFace(vertex1, vertex2, vertex3, uTarget, vTarget, worldMatrix, scene, debugVisuals) {
    vertex1.position.applyMatrix4(worldMatrix);
    vertex2.position.applyMatrix4(worldMatrix);
    vertex3.position.applyMatrix4(worldMatrix);
    const tri = new Triangle(vertex1.position, vertex2.position, vertex3.position);
    const normal = new Vector3();
    tri.getNormal(normal);
    const point = weightedAverage(vertex1, vertex2, vertex3, uTarget, vTarget, scene, debugVisuals);
    if (debugVisuals) {
        createLine(point, normal.clone().multiplyScalar(10).add(point), scene, 0x333333);
    }
    return { position: point, normal };
}
export function showNormals(geometry, worldMatrix, scene) {
    const positionBuffer = geometry.getAttribute("position");
    const normalBuffer = geometry.getAttribute("normal");
    // For indexed geometry use the index array to compute the correct relative indices for both the
    // uv and buffer arrays internally.
    if (geometry.index) {
        for (let i = 0; i < geometry.index.array.length; i += 3) {
            const index1 = geometry.index.array[i];
            const index2 = geometry.index.array[i + 1];
            const index3 = geometry.index.array[i + 2];
            const vertex1 = new Vector3(positionBuffer.array[index1 * 3], positionBuffer.array[index1 * 3 + 1], positionBuffer.array[index1 * 3 + 2]);
            const vertex2 = new Vector3(positionBuffer.array[index2 * 3], positionBuffer.array[index2 * 3 + 1], positionBuffer.array[index2 * 3 + 2]);
            const vertex3 = new Vector3(positionBuffer.array[index3 * 3], positionBuffer.array[index3 * 3 + 1], positionBuffer.array[index3 * 3 + 2]);
            vertex1.applyMatrix4(worldMatrix);
            vertex2.applyMatrix4(worldMatrix);
            vertex3.applyMatrix4(worldMatrix);
            // Get normals
            const normal1 = new Vector3(normalBuffer.array[index1 * 3], normalBuffer.array[index1 * 3 + 1], normalBuffer.array[index1 * 3 + 2]);
            const normal2 = new Vector3(normalBuffer.array[index2 * 3], normalBuffer.array[index2 * 3 + 1], normalBuffer.array[index2 * 3 + 2]);
            const normal3 = new Vector3(normalBuffer.array[index3 * 3], normalBuffer.array[index3 * 3 + 1], normalBuffer.array[index3 * 3 + 2]);
            const tri = new Triangle(vertex1, vertex2, vertex3);
            const normal = new Vector3();
            tri.getNormal(normal);
            const point = new Vector3();
            tri.getMidpoint(point);
            createLine(point, normal.multiplyScalar(10).add(point), scene, 0x333333);
            createLine(vertex1, normal1.multiplyScalar(10).add(vertex1), scene, 0x00ff00);
            createLine(vertex2, normal2.multiplyScalar(10).add(vertex2), scene, 0x0000ff);
            createLine(vertex3, normal3.multiplyScalar(10).add(vertex3), scene, 0x00ffff);
        }
    }
    else {
        // For non-indexed geometry, the uv and position arrays should share the same relative indices
        for (let i = 0; i < positionBuffer.array.length; i += 9) {
            const vertex1 = new Vector3(positionBuffer.array[i], positionBuffer.array[i + 1], positionBuffer.array[i + 2]);
            const vertex2 = new Vector3(positionBuffer.array[i + 3], positionBuffer.array[i + 4], positionBuffer.array[i + 5]);
            const vertex3 = new Vector3(positionBuffer.array[i + 6], positionBuffer.array[i + 7], positionBuffer.array[i + 8]);
            vertex1.applyMatrix4(worldMatrix);
            vertex2.applyMatrix4(worldMatrix);
            vertex3.applyMatrix4(worldMatrix);
            // Get normals
            const normal1 = new Vector3(normalBuffer.array[Math.round(i * 1.5)], normalBuffer.array[Math.round(i * 1.5) + 1], normalBuffer.array[Math.round(i * 1.5) + 2]);
            const normal2 = new Vector3(normalBuffer.array[Math.round(i * 1.5) + 3], normalBuffer.array[Math.round(i * 1.5) + 4], normalBuffer.array[Math.round(i * 1.5) + 5]);
            const normal3 = new Vector3(normalBuffer.array[Math.round(i * 1.5) + 6], normalBuffer.array[Math.round(i * 1.5) + 7], normalBuffer.array[Math.round(i * 1.5) + 8]);
            const tri = new Triangle(vertex1, vertex2, vertex3);
            const normal = new Vector3();
            tri.getNormal(normal);
            const point = new Vector3();
            tri.getMidpoint(point);
            createLine(point, normal.multiplyScalar(10).add(point), scene, 0x333333);
            createLine(vertex1, normal1.multiplyScalar(10).add(vertex1), scene, 0x00ff00);
            createLine(vertex2, normal2.multiplyScalar(10).add(vertex2), scene, 0x0000ff);
            createLine(vertex3, normal3.multiplyScalar(10).add(vertex3), scene, 0x00ffff);
        }
    }
}
export function showVertices(geometry, worldMatrix, scene) {
    for (let i = 0; i < geometry.vertices.length; i++) {
        const vertex = geometry.vertices[i].clone();
        vertex.applyMatrix4(worldMatrix);
        createSphere(vertex, scene, 0x0000ff);
    }
}
function fromUVOnGeometry(u, v, geometry, worldMatrix, scene, debugVisuals) {
    // showVertices(geometry, worldMatrix, scene);
    for (let i = 0; i < geometry.faces.length; i++) {
        const face = geometry.faces[i];
        const u1 = geometry.faceVertexUvs[0][i][0].x;
        const v1 = geometry.faceVertexUvs[0][i][0].y;
        // Vertex 2
        const u2 = geometry.faceVertexUvs[0][i][1].x;
        const v2 = geometry.faceVertexUvs[0][i][1].y;
        // Vertex 3
        const u3 = geometry.faceVertexUvs[0][i][2].x;
        const v3 = geometry.faceVertexUvs[0][i][2].y;
        // Determine the min and max uv for this triangle
        const minU = Math.min(u1, u2, u3);
        const maxU = Math.max(u1, u2, u3);
        const minV = Math.min(v1, v2, v3);
        const maxV = Math.max(v1, v2, v3);
        if (u >= minU && u <= maxU && v >= minV && v <= maxV) {
            const vertex1 = geometry.vertices[face.a].clone();
            const vertex2 = geometry.vertices[face.b].clone();
            const vertex3 = geometry.vertices[face.c].clone();
            return pointNormalFromFace({ position: vertex1, u: u1, v: v1 }, { position: vertex2, u: u2, v: v2 }, { position: vertex3, u: u3, v: v3 }, u, v, worldMatrix, scene, debugVisuals);
        }
    }
    return undefined;
}
function fromUVOnBuffer(u, v, geometry, worldMatrix, scene, debugVisuals) {
    // Get the uv and position buffers from the geometry. These are defined as arrays of consecutive floats in pairs of 2 for uvs or 6 for vertices
    const uvBuffer = geometry.getAttribute("uv");
    const positionBuffer = geometry.getAttribute("position");
    // For indexed geometry use the index array to compute the correct relative indices for both the
    // uv and buffer arrays internally.
    if (geometry.index) {
        for (let i = 0; i < geometry.index.array.length; i += 3) {
            const index1 = geometry.index.array[i];
            const index2 = geometry.index.array[i + 1];
            const index3 = geometry.index.array[i + 2];
            // Vertex 1
            const u1 = uvBuffer.array[index1 * 2];
            const v1 = uvBuffer.array[index1 * 2 + 1];
            // Vertex 2
            const u2 = uvBuffer.array[index2 * 2];
            const v2 = uvBuffer.array[index2 * 2 + 1];
            // Vertex 3
            const u3 = uvBuffer.array[index3 * 2];
            const v3 = uvBuffer.array[index3 * 2 + 1];
            // Determine the min and max uv for this triangle
            const minU = Math.min(u1, u2, u3);
            const maxU = Math.max(u1, u2, u3);
            const minV = Math.min(v1, v2, v3);
            const maxV = Math.max(v1, v2, v3);
            // If the target uv is inside this triangle index into it's position buffer
            // to find the position and transform it world space
            if (u >= minU && u <= maxU && v >= minV && v <= maxV) {
                const vertex1 = new Vector3(positionBuffer.array[index1 * 3], positionBuffer.array[index1 * 3 + 1], positionBuffer.array[index1 * 3 + 2]);
                const vertex2 = new Vector3(positionBuffer.array[index2 * 3], positionBuffer.array[index2 * 3 + 1], positionBuffer.array[index2 * 3 + 2]);
                const vertex3 = new Vector3(positionBuffer.array[index3 * 3], positionBuffer.array[index3 * 3 + 1], positionBuffer.array[index3 * 3 + 2]);
                return pointNormalFromFace({ position: vertex1, u: u1, v: v1 }, { position: vertex2, u: u2, v: v2 }, { position: vertex3, u: u3, v: v3 }, u, v, worldMatrix, scene, debugVisuals);
            }
        }
    }
    else {
        // For non-indexed geometry, the uv and position arrays should share the same relative indices
        for (let i = 0; i < uvBuffer.array.length; i += 6) {
            // Vertex 1
            const u1 = uvBuffer.array[i];
            const v1 = uvBuffer.array[i + 1];
            // Vertex 2
            const u2 = uvBuffer.array[i + 2];
            const v2 = uvBuffer.array[i + 3];
            // Vertex 3
            const u3 = uvBuffer.array[i + 4];
            const v3 = uvBuffer.array[i + 5];
            // Determine the min and max uv for this triangle
            const minU = Math.min(u1, u2, u3);
            const maxU = Math.max(u1, u2, u3);
            const minV = Math.min(v1, v2, v3);
            const maxV = Math.max(v1, v2, v3);
            // If the target uv is inside this triangle index into it's position buffer
            // to find the position and transform it world space
            if (u >= minU && u <= maxU && v >= minV && v <= maxV) {
                const vertex1 = new Vector3(positionBuffer.array[Math.round(i * 1.5)], positionBuffer.array[Math.round(i * 1.5) + 1], positionBuffer.array[Math.round(i * 1.5) + 2]);
                const vertex2 = new Vector3(positionBuffer.array[Math.round(i * 1.5) + 3], positionBuffer.array[Math.round(i * 1.5) + 4], positionBuffer.array[Math.round(i * 1.5) + 5]);
                const vertex3 = new Vector3(positionBuffer.array[Math.round(i * 1.5) + 6], positionBuffer.array[Math.round(i * 1.5) + 7], positionBuffer.array[Math.round(i * 1.5) + 8]);
                return pointNormalFromFace({ position: vertex1, u: u1, v: v1 }, { position: vertex2, u: u2, v: v2 }, { position: vertex3, u: u3, v: v3 }, u, v, worldMatrix, scene, debugVisuals);
            }
        }
    }
    return undefined;
}
export function pointNormalFromUV(u, v, geometry, worldMatrix, scene, debugVisuals) {
    if (geometry instanceof BufferGeometry) {
        return fromUVOnBuffer(u, v, geometry, worldMatrix, scene, debugVisuals);
    }
    if (geometry instanceof Geometry) {
        return fromUVOnGeometry(u, v, geometry, worldMatrix, scene, debugVisuals);
    }
    return undefined;
}
//# sourceMappingURL=geometryHelper.js.map